// Feb 2026 — Transferable Skills Reflection questionnaire page.
//
// Multi-part tap-only year-end reflection for Ontario Grades 4-8.
// Three sequential parts (Part 1: 7 skills × items rated 1-4; Part 2:
// looking back; Part 3: looking forward) + email + consent at submit.
// Result is rendered on the token URL `/reflection/{token}`.
//
// Spec: transferable_skills_reflection_kit.md (v2).

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import useDocumentMeta from "../lib/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCALE_OPTIONS = [
  { value: 1, label: "1", caption: "Rarely got to it" },
  { value: 2, label: "2", caption: "Occasionally" },
  { value: 3, label: "3", caption: "Regularly" },
  { value: 4, label: "4", caption: "Woven through my program" },
];

// Small primitives ----------------------------------------------------------

function SectionWrapper({ eyebrow, title, intro, children }) {
  return (
    <section className="mt-10 max-w-3xl">
      <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
        {eyebrow}
      </p>
      <h2 className="mt-2 font-black text-2xl sm:text-3xl text-[#1c2d3d] tracking-tight">
        {title}
      </h2>
      {intro ? (
        <p className="mt-3 text-[#4a6984]/85 leading-relaxed">{intro}</p>
      ) : null}
      <div className="mt-5">{children}</div>
    </section>
  );
}

function ScaleRow({ item, value, onChange }) {
  return (
    <div
      data-testid={`scale-row-${item.id}`}
      className="py-3 border-b border-[#4a6984]/10 last:border-b-0"
    >
      <p className="text-sm text-[#1c2d3d] leading-snug mb-2.5">{item.text}</p>
      <div className="flex gap-1.5 flex-wrap">
        {SCALE_OPTIONS.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              data-testid={`scale-btn-${item.id}-${opt.value}`}
              title={opt.caption}
              className={`h-9 w-9 rounded-md font-extrabold text-sm transition border ${
                active
                  ? "bg-[#1c2d3d] text-white border-[#1c2d3d]"
                  : "bg-white text-[#1c2d3d] border-[#4a6984]/25 hover:border-[#1c2d3d]"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CheckChip({ checked, onToggle, children, testid, disabled }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      data-testid={testid}
      aria-pressed={checked}
      className={`text-left text-sm leading-snug px-3.5 py-2 rounded-full border transition font-bold ${
        checked
          ? "bg-[#1c2d3d] text-white border-[#1c2d3d]"
          : "bg-white text-[#1c2d3d] border-[#4a6984]/25 hover:border-[#1c2d3d]"
      } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );
}

function RadioChip({ checked, onSelect, children, testid }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={testid}
      aria-pressed={checked}
      className={`text-left text-sm leading-snug px-3.5 py-2 rounded-full border transition font-bold ${
        checked
          ? "bg-[#3EB489] text-white border-[#3EB489]"
          : "bg-white text-[#1c2d3d] border-[#4a6984]/25 hover:border-[#3EB489]"
      }`}
    >
      {children}
    </button>
  );
}

// Page ----------------------------------------------------------------------

export default function ReflectionPage() {
  useDocumentMeta({
    title: "Transferable skills year-end reflection — Atomic Edu",
    description:
      "A short, tap-only year-end reflection for Ontario Grades 4-8 teachers, built on the seven transferable skills.",
  });
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Part 1 — item_id → rating.
  const [ratings, setRatings] = useState({});
  // Part 2 — most-natural & hardest-visible: arrays of skill_ids (max 2 each).
  const [mostNatural, setMostNatural] = useState([]);
  const [hardestVisible, setHardestVisible] = useState([]);
  const [gotInWay, setGotInWay] = useState([]);
  // Part 3.
  const [oneSkill, setOneSkill] = useState(null);
  const [makeReal, setMakeReal] = useState([]);
  const [support, setSupport] = useState([]);
  // Identification + consent.
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reflection/schema`);
        if (!cancelled) setSchema(data);
      } catch {
        if (!cancelled) setError("Couldn't load the questionnaire — please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totalRated = useMemo(
    () => Object.values(ratings).filter((v) => v >= 1 && v <= 4).length,
    [ratings],
  );

  const toggleInArray = (setter, max) => (value) => {
    setter((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      if (max && prev.length >= max) return prev;
      return [...prev, value];
    });
  };

  const canSubmit =
    totalRated > 0 && firstName.trim().length > 0 && email.trim().length > 0 && consent;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/reflection/submit`, {
        first_name: firstName.trim(),
        email: email.trim(),
        consent,
        item_ratings: ratings,
        part2_most_natural: mostNatural,
        part2_hardest_visible: hardestVisible,
        part2_got_in_way: gotInWay,
        part3_one_skill: oneSkill,
        part3_make_real: makeReal,
        part3_support: support,
      });
      navigate(data.redirect_url || `/reflection/${data.token}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't submit — please try again.";
      setError(typeof msg === "string" ? msg : "Couldn't submit — please try again.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] flex items-center justify-center text-[#4a6984]">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading the reflection…
      </div>
    );
  }
  if (!schema) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] flex items-center justify-center text-rose-700 text-sm">
        {error || "Reflection unavailable."}
      </div>
    );
  }

  const skillLabel = (id) => schema.skills.find((s) => s.id === id)?.label || id;

  return (
    <div className="min-h-screen bg-[#f4f5f7]">
      <header className="bg-[#1c2d3d] text-white">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <a href="/" className="text-sm font-extrabold tracking-tight">
            ← Atomic Edu
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pt-10 pb-20">
        {/* HERO */}
        <section data-testid="reflection-hero">
          <p className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/75 flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5" /> Year-end · free for Ontario teachers
          </p>
          <h1
            data-testid="reflection-hero-headline"
            className="mt-3 font-black text-3xl sm:text-4xl text-[#1c2d3d] tracking-tight leading-[1.1]"
          >
            Transferable Skills Reflection
          </h1>
          <p className="mt-4 text-[#4a6984]/85 leading-relaxed">
            These seven skills are the spine of the Ontario curriculum — what
            students carry out of your room into every subject and every year
            after. This isn&apos;t a grade on your class. It&apos;s an honest read
            on how visibly you built these in this year, and a chance to decide
            what next year should feel like — while it&apos;s still fresh.{" "}
            <span className="font-extrabold text-[#1c2d3d]">
              Tap through it; nothing here needs writing.
            </span>
          </p>
        </section>

        <form onSubmit={handleSubmit}>
          {/* Part 1 — skill sections */}
          <SectionWrapper
            eyebrow="Part 1"
            title="This year, skill by skill"
            intro="For each item, tap 1–4. 1 = rarely got to it; 4 = woven through your program."
          >
            <div className="space-y-7" data-testid="part1-sections">
              {schema.skills.map((skill) => (
                <div
                  key={skill.id}
                  data-testid={`skill-section-${skill.id}`}
                  className="ae-card p-5 sm:p-6 bg-white"
                >
                  <h3 className="font-extrabold text-lg text-[#1c2d3d] tracking-tight">
                    {skill.label}
                  </h3>
                  <p className="text-sm text-[#4a6984]/85 italic mt-1.5">
                    {skill.plain_meaning}
                  </p>
                  <div className="mt-3">
                    {skill.items.map((item) => (
                      <ScaleRow
                        key={item.id}
                        item={item}
                        value={ratings[item.id]}
                        onChange={(v) =>
                          setRatings((prev) => ({ ...prev, [item.id]: v }))
                        }
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </SectionWrapper>

          {/* Part 2 */}
          <SectionWrapper eyebrow="Part 2" title="Looking back">
            <div className="space-y-7">
              <div>
                <p className="font-extrabold text-sm text-[#1c2d3d] mb-2">
                  Which skills showed up most naturally in your room this year?{" "}
                  <span className="font-medium text-[#4a6984]/70">
                    (pick up to 2)
                  </span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {schema.skills.map((s) => (
                    <CheckChip
                      key={s.id}
                      testid={`part2-natural-${s.id}`}
                      checked={mostNatural.includes(s.id)}
                      onToggle={() => toggleInArray(setMostNatural, 2)(s.id)}
                      disabled={!mostNatural.includes(s.id) && mostNatural.length >= 2}
                    >
                      {skillLabel(s.id)}
                    </CheckChip>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-extrabold text-sm text-[#1c2d3d] mb-2">
                  Which were hardest to make visible, even when you wanted to?{" "}
                  <span className="font-medium text-[#4a6984]/70">(pick up to 2)</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {schema.skills.map((s) => (
                    <CheckChip
                      key={s.id}
                      testid={`part2-hardest-${s.id}`}
                      checked={hardestVisible.includes(s.id)}
                      onToggle={() => toggleInArray(setHardestVisible, 2)(s.id)}
                      disabled={
                        !hardestVisible.includes(s.id) && hardestVisible.length >= 2
                      }
                    >
                      {skillLabel(s.id)}
                    </CheckChip>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-extrabold text-sm text-[#1c2d3d] mb-2">
                  What got in the way most this year?{" "}
                  <span className="font-medium text-[#4a6984]/70">(pick any)</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {schema.part2_got_in_way.map((o) => (
                    <CheckChip
                      key={o.id}
                      testid={`part2-gotinway-${o.id}`}
                      checked={gotInWay.includes(o.id)}
                      onToggle={() => toggleInArray(setGotInWay)(o.id)}
                    >
                      {o.text}
                    </CheckChip>
                  ))}
                </div>
              </div>
            </div>
          </SectionWrapper>

          {/* Part 3 */}
          <SectionWrapper
            eyebrow="Part 3"
            title="Looking forward"
            intro="The half that matters most. Pick one skill to deliberately build in from week one next year — then say what would make it real for you."
          >
            <div className="space-y-7">
              <div>
                <p className="font-extrabold text-sm text-[#1c2d3d] mb-2">
                  Which one skill do you most want to build in deliberately, from
                  week one?{" "}
                  <span className="font-medium text-[#4a6984]/70">(pick 1)</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {schema.skills.map((s) => (
                    <RadioChip
                      key={s.id}
                      testid={`part3-oneskill-${s.id}`}
                      checked={oneSkill === s.id}
                      onSelect={() => setOneSkill(s.id)}
                    >
                      {skillLabel(s.id)}
                    </RadioChip>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-extrabold text-sm text-[#1c2d3d] mb-2">
                  What would make that real in your classroom?{" "}
                  <span className="font-medium text-[#4a6984]/70">(pick any)</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {schema.part3_make_real.map((o) => (
                    <CheckChip
                      key={o.id}
                      testid={`part3-makereal-${o.id}`}
                      checked={makeReal.includes(o.id)}
                      onToggle={() => toggleInArray(setMakeReal)(o.id)}
                    >
                      {o.text}
                    </CheckChip>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-extrabold text-sm text-[#1c2d3d] mb-2">
                  What kind of support would actually help you do it?{" "}
                  <span className="font-medium text-[#4a6984]/70">(pick any)</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {schema.part3_support.map((o) => (
                    <CheckChip
                      key={o.id}
                      testid={`part3-support-${o.id}`}
                      checked={support.includes(o.id)}
                      onToggle={() => toggleInArray(setSupport)(o.id)}
                    >
                      {o.text}
                    </CheckChip>
                  ))}
                </div>
              </div>
            </div>
          </SectionWrapper>

          {/* Submit block */}
          <SectionWrapper eyebrow="Last step" title="Save and send back to me">
            <div className="ae-card p-5 sm:p-6 bg-white space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="rfx-first-name"
                    className="block text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#4a6984]/75 mb-1.5"
                  >
                    First name
                  </label>
                  <input
                    id="rfx-first-name"
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    data-testid="reflection-first-name"
                    className="w-full px-3.5 py-2.5 rounded-md border border-[#4a6984]/25 bg-white text-[#1c2d3d] font-medium focus:outline-none focus:border-[#1c2d3d]"
                  />
                </div>
                <div>
                  <label
                    htmlFor="rfx-email"
                    className="block text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#4a6984]/75 mb-1.5"
                  >
                    Email
                  </label>
                  <input
                    id="rfx-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="reflection-email"
                    className="w-full px-3.5 py-2.5 rounded-md border border-[#4a6984]/25 bg-white text-[#1c2d3d] font-medium focus:outline-none focus:border-[#1c2d3d]"
                  />
                </div>
              </div>
              <label className="flex items-start gap-3 text-sm text-[#4a6984]/85 leading-snug cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="reflection-consent"
                  className="mt-1 h-4 w-4 rounded border-[#4a6984]/40"
                />
                <span>
                  Send my reflection and keep me posted with useful
                  back-to-school resources from Atomic Edu.{" "}
                  <span className="text-[#4a6984]/70">
                    (You can unsubscribe any time.)
                  </span>
                </span>
              </label>
              {error ? (
                <p
                  className="text-sm text-rose-700"
                  data-testid="reflection-submit-error"
                >
                  {error}
                </p>
              ) : null}
              <Button
                type="submit"
                disabled={!canSubmit || submitting}
                data-testid="reflection-submit"
                className="h-12 px-6 rounded-md font-extrabold inline-flex items-center justify-center gap-2 bg-[#1c2d3d] text-white hover:bg-[#0f1e2c] disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating your reflection…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Show me my reflection <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
              <p className="text-xs text-[#4a6984]/65">
                {totalRated > 0 ? `${totalRated} item${totalRated === 1 ? "" : "s"} rated` : "Tap a 1–4 on any item above to get started."}
              </p>
            </div>
          </SectionWrapper>
        </form>
      </main>
    </div>
  );
}
